import React, { useState } from 'react';
import { Box, Container, Typography, Alert, Snackbar } from '@mui/material';
import { useTranslation } from 'react-i18next';
import OrchestrationQueryForm from '../components/Query/OrchestrationQueryForm';
import OrchestrationResults from '../components/Query/OrchestrationResults';
import { queryApi } from '../services/api';

const OrchestrationQueryPage = () => {
  const { t } = useTranslation();
  const [orchestrationData, setOrchestrationData] = useState(null);
  const [synthesisData, setSynthesisData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [synthesizing, setSynthesizing] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const handleSearch = async (searchRequest) => {
    setLoading(true);
    setError(null);
    setOrchestrationData(null);
    setSynthesisData(null);

    try {
      console.log('Executing orchestrated search:', searchRequest);
      const response = await queryApi.orchestrateSearch(searchRequest);
      console.log('Orchestration response:', response);
      
      setOrchestrationData(response);
      
      // Show success message
      const successfulServices = response.services_used.filter(
        service => !response.errors[service]
      );
      setSuccessMessage(
        `Search completed! Found results from ${successfulServices.length} out of ${response.services_used.length} sources.`
      );
    } catch (err) {
      console.error('Orchestration error:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to execute search. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSynthesize = async (selectedResults, originalQuery) => {
    if (!selectedResults || selectedResults.length === 0) {
      setError('No results selected for synthesis.');
      return;
    }

    setSynthesizing(true);
    setError(null);

    try {
      console.log('Synthesizing results:', selectedResults);
      
      const synthesisRequest = {
        selected_results: selectedResults,
        query: originalQuery,
        synthesis_prompt: null // Use default prompt
      };

      const response = await queryApi.synthesizeAnswer(synthesisRequest);
      console.log('Synthesis response:', response);
      
      setSynthesisData(response);
      setSuccessMessage('Answer synthesized successfully!');
    } catch (err) {
      console.error('Synthesis error:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to synthesize answer. Please try again.';
      setError(errorMessage);
    } finally {
      setSynthesizing(false);
    }
  };

  const handleError = (errorMessage) => {
    setError(errorMessage);
  };

  const handleCopyAnswer = () => {
    setSuccessMessage('Answer copied to clipboard!');
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage('');
    setError(null);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          {t('orchestration.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('orchestration.subtitle')}
        </Typography>
      </Box>

      {/* Search Form */}
      <OrchestrationQueryForm 
        onResultsReceived={handleSearch}
        onError={handleError}
      />

      {/* Search Results */}
      <OrchestrationResults
        orchestrationData={orchestrationData}
        loading={loading}
        onSynthesize={handleSynthesize}
        synthesizing={synthesizing}
        synthesisData={synthesisData}
      />



      {/* Error Display */}
      {error && !loading && (
        <Alert 
          severity="error" 
          sx={{ mt: 3, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OrchestrationQueryPage;
